import { useState } from "react";
import { Button, Col, Form, InputNumber, Row, Select, Typography } from "antd";
import { Table } from "antd";
import { BOM } from "../../../../services/definedMethod";
import cnt from "../../../../services/definedConstant";
import { DeleteOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

const parseAmount = (value) => Number(String(value ?? "").replace(/,/g, ""));

export default function CurrencyDenominationInfo({
  form,
  currencyOptions,
  denominationOptions,
  denominationEntries,
  setDenominationEntries,
  allowDenomTracking,
}) {
  const allowDenom = allowDenomTracking;

  const handleAddDenomination = () => {
    const fields = ["currency"];
    if (allowDenom !== false) {
      fields.push("currencyDenomination", "denominationCount");
    } else {
      fields.push("denominationValue");
    }

    form.validateFields(fields).then((values) => {
      const {
        currency,
        currencyDenomination,
        denominationCount,
        denominationValue,
      } = values;
      const denomAmount =
        allowDenom !== false
          ? parseAmount(currencyDenomination) * Number(denominationCount)
          : parseAmount(denominationValue);

      if (
        allowDenom !== false &&
        (!Number.isFinite(denomAmount) || denomAmount < 0)
      ) {
        BOM.AlertMsg(
          cnt.WARNING,
          "Please select a valid denomination and count",
        );
        return;
      }

      if (
        allowDenom === false &&
        (!Number.isFinite(parseAmount(denominationValue)) ||
          parseAmount(denominationValue) < 0)
      ) {
        BOM.AlertMsg(cnt.WARNING, "Please enter a valid denomination value");
        return;
      }

      const newEntry = {
        key: Date.now(),
        currency: currencyOptions.find((c) => c.value === currency)?.label,
        denomination: allowDenom !== false ? currencyDenomination : 0,
        count: allowDenom !== false ? denominationCount : 0,
        value: denomAmount,
      };
      setDenominationEntries([...denominationEntries, newEntry]);

      const resetVals =
        allowDenom !== false
          ? {
              denominationCount: undefined,
              currencyDenomination: undefined,
            }
          : { denominationValue: undefined };
      form.setFieldsValue({
        ...resetVals,
        ...(allowDenom !== false && { denominationValue: undefined }),
      });
    });
  };
  const denominationColumns = [
    { title: "Currency", dataIndex: "currency", key: "currency" },
    { title: "Denomination", dataIndex: "denomination", key: "denomination" },
    { title: "Count", dataIndex: "count", key: "count" },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (value) => BOM.FormatAmount(value),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          className="of-primaryBtnColor"
          icon={<DeleteOutlined />}
          onClick={() =>
            setDenominationEntries(
              denominationEntries.filter((e) => e.key !== record.key),
            )
          }
        />
      ),
    },
  ];
  const handleDenominationChange = (value) => {
    const count = form.getFieldValue("denominationCount");
    if (
      value !== undefined &&
      value !== null &&
      count !== undefined &&
      count !== null
    ) {
      form.setFieldsValue({ denominationValue: parseAmount(value) * count });
    }
  };

  const handleCountChange = (value) => {
    const denom = form.getFieldValue("currencyDenomination");
    if (
      denom !== undefined &&
      denom !== null &&
      value !== undefined &&
      value !== null
    ) {
      form.setFieldsValue({ denominationValue: parseAmount(denom) * value });
    }
  };
  return (
    <div>
      <div>
        <Title level={5}>Currency Denomination Info</Title>
        <Row gutter={16} align="bottom" style={{ marginBottom: 12 }}>
          <Col span={6}>
            <Form.Item
              name="currency"
              label={<Text strong>Currency</Text>}
              rules={[
                {
                  required: true,
                  message: "Currency is required",
                },
              ]}
            >
              <Select
                style={{ width: "100%", height: 44 }}
                options={currencyOptions}
                placeholder="Select currency"
                showSearch
                allowClear
                onChange={() => {
                  form.setFieldsValue({
                    currencyDenomination: undefined,
                    denominationCount: undefined,
                    denominationValue: undefined,
                  });
                }}
              />
            </Form.Item>
          </Col>

          {allowDenom !== false && (
            <Col span={6}>
              <Form.Item
                name="currencyDenomination"
                label={<Text strong>Currency Denomination</Text>}
                rules={[
                  {
                    required: true,
                    message: "Currency denomination is required",
                  },
                ]}
              >
                <Select
                  style={{ width: "100%", height: 44 }}
                  options={denominationOptions}
                  placeholder="Select denomination"
                  onChange={handleDenominationChange}
                  showSearch
                  allowClear
                />
              </Form.Item>
            </Col>
          )}

          {allowDenom !== false && (
            <Col span={6}>
              <Form.Item
                name="denominationCount"
                label={<Text strong>Denomination count</Text>}
                rules={[
                  {
                    required: true,
                    message: "Denomination count is required",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%", height: 44, paddingTop: 6 }}
                  placeholder="0"
                  onChange={handleCountChange}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={6}>
            <Form.Item
              name="denominationValue"
              label={<Text strong>Denomination value</Text>}
              rules={
                allowDenom === false
                  ? [
                      {
                        required: true,
                        message: "Denomination value is required",
                      },
                    ]
                  : []
              }
            >
              <InputNumber
                style={{ width: "100%", height: 44, paddingTop: 6 }}
                placeholder="0"
                className="of-bold-disable-input"
                formatter={BOM.FormatAmount}
                parser={BOM.ParseAmount}
                disabled={allowDenom !== false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={24}>
            <Button
              type="primary"
              className="of-primaryBtnColor"
              style={{ height: 44 }}
              htmlType="button"
              onClick={handleAddDenomination}
            >
              Add
            </Button>
          </Col>
        </Row>
      </div>

      {denominationEntries.length > 0 && (
        <Table
          style={{ marginTop: 16, marginBottom: 24 }}
          dataSource={denominationEntries}
          columns={denominationColumns}
          pagination={false}
          size="small"
        />
      )}
    </div>
  );
}
